<template>
  <div class="mt-4 bg-white shadow rounded-lg overflow-hidden">
    <div
      class="bg-white p-6"
      v-if="statistics"
    >
      <div class="mb-6 flex justify-end">
        <div class="w-64">
          <label for="result-by" class="block text-sm font-medium text-gray-700 mb-2">Group Results By</label>
          <select
            id="result-by"
            v-model="result_by"
            @change="loadChart"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white transition-colors duration-200"
          >
            <option value="sample_id">Individual Samples</option>
            <option value="zone_id">Management Zones</option>
          </select>
        </div>
      </div>
      <div class="space-y-6">
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="bg-white rounded-md shadow-sm border p-3">
            <div class="relative w-full transition-all duration-300 ease-in-out" 
                 :style="{ height: chartHeight + 'px', minHeight: '500px' }"
                 ref="chartContainer">
              <canvas 
                ref="chartCanvas"
                id="map-graph" 
                class="absolute inset-0 w-full h-full"
              ></canvas>
              <PrintMapChart
                :statistics="statistics"
                :width="'100%'"
                :height="'100%'"
                :key="chartKey"
              />
            </div>
          </div>
        </div>

        <div class="legend-section">
          <h3 class="text-sm font-semibold text-gray-700 mb-3 text-center">Classification Legend</h3>
          <div class="flex flex-wrap justify-center gap-3">
            <div
              class="flex items-center space-x-2 px-3 py-2 bg-white rounded-md shadow-sm border hover:shadow-md transition-shadow duration-200"
              v-for="(legend, index) in Object.entries(chartKeys)"
              :key="index"
            >
              <div
                class="w-4 h-4 rounded-sm border border-gray-200"
                :style="{ backgroundColor: `rgb(${legend[1]})` }"
              ></div>
              <span class="text-sm font-medium text-gray-900">{{ legend[0] }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="bg-white p-12">
      <div class="text-center">
        <div class="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No Analysis Data Available</h3>
        <p class="text-gray-600 max-w-md mx-auto">
          No soil analysis results found. Please check your sampling data or try selecting a different soil property from the map.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import PrintMapChart from "@/Print/PrintMapChart.vue";
import { Inertia } from "@inertiajs/inertia";
import { reactive } from 'vue';
import { soilAnalysisStore } from '../../../stores/soilAnalysisStore.js';

export default {
  components: { PrintMapChart },
  props: {
    chartDetails: [Object, Array],
    task: [String, Number],
    farm: [String, Number],
    chartLegend: [Array, Object]
  },

  data() {
    return {
      statistics: null,
      soilType: null,
      selectedSoilType: "ph_water",
      result_by: "sample_id",
      chartKeys: [],
      watcherUnsubscribe: null, // To store the watcher unsubscribe function
      chartKey: 0, // Force re-render when dimensions change
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      resizeTimeout: null, // For debouncing resize events
    };
  },

  watch: {},

  computed: {
    chartWidth() {
      // Use container-based width, responsive to window size
      const containerWidth = Math.max(this.windowWidth - 400, 600);
      const width = Math.min(containerWidth, 1200);
      return width;
    },
    chartHeight() {
      // Ensure we have a valid width before calculating height
      const width = this.chartWidth || 600; // Fallback to 600px if width not ready
      
      // Maintain a good aspect ratio - reduced to 1.4 for much taller chart
      let height = Math.round(width / 1.4);
      
      // Ensure minimum height for better readability - increased to 500px
      height = Math.max(height, 500);
      
      return height;
    }
  },

  mounted() {
    // Set correct initial window width
    this.windowWidth = window.innerWidth;
    
    // Add window resize listener with proper debouncing
    this.resizeTimeout = null;
    this.handleResize = () => {
      const newWidth = window.innerWidth;
      
      // Only proceed if width actually changed
      if (newWidth === this.windowWidth) {
        return; // No change, don't process
      }
      
      // Clear any pending resize handling
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      // Debounce the resize handling
      this.resizeTimeout = setTimeout(() => {
        this.windowWidth = newWidth;
        this.chartKey++;
        this.resizeTimeout = null;
      }, 150); // 150ms debounce
    };
    window.addEventListener('resize', this.handleResize);
    
    if (this.chartDetails && this.chartDetails.statistics) {
      this.statistics = this.chartDetails.statistics;
    }
    if (this.chartDetails && this.chartDetails.chart_unit) {
      this.soilType = this.chartDetails.chart_unit;
    }
    if (this.chartLegend) {
      // Handle both array and object formats
      if (Array.isArray(this.chartLegend)) {
        this.chartKeys = this.chartLegend;
      } else if (typeof this.chartLegend === 'object') {
        // Convert object to array format like: [["key", "value"], ...]
        this.chartKeys = Object.entries(this.chartLegend);
      }
    }
    
    // Initialize from existing store state if available
    if (soilAnalysisStore.selectedSoilType) {
      this.selectedSoilType = soilAnalysisStore.selectedSoilType;
    }
    
    // Update store with current component state if it's not set
    if (!soilAnalysisStore.selectedSoilType && this.selectedSoilType) {
      soilAnalysisStore.updateSelectedSoilType(this.selectedSoilType);
    }
    
    // Set soil analysis context for prop system (used by other components via props)
    // Note: Reactivity is handled by the store, this is just for prop data
    if (this.task && this.farm && this.soilType) {
      this.$root.soilAnalysisContext = {
        task: this.task,
        farm: this.farm,
        soilTypes: this.soilType,
        selectedSoilType: this.selectedSoilType
      };
    }
    
    // Watch for external changes to sample zones and update chart accordingly
    
    this.watcherUnsubscribe = this.$watch(
      () => soilAnalysisStore.selectedSoilType,
      (newSoilType, oldSoilType) => {
        if (newSoilType && newSoilType !== this.selectedSoilType) {
          this.selectedSoilType = newSoilType;
          // Reload chart with new soil type
          this.loadChart();
        }
      },
      { immediate: true, deep: false }
    );
    
    // If no initial data provided, load it
    if (!this.statistics || !this.soilType || !this.chartKeys.length) {
      this.loadChart();
    } else {
      // If we have initial data, ensure proper sizing after DOM is ready
      this.$nextTick(() => {
        setTimeout(() => {
          // Only force initial refresh if we haven't received any store updates yet
          if (this.selectedSoilType === "ph_water") { // Default value, meaning no store update happened
            // Force a chart refresh to ensure proper initial sizing
            this.chartKey++;
          }
        }, 150); // Slightly longer delay to ensure container height is calculated
      });
    }
  },
  watch: {
    // Watch for changes to chart details prop
    chartDetails: {
      handler(newChartDetails) {
        if (newChartDetails && newChartDetails.statistics) {
          this.statistics = newChartDetails.statistics;
        }
        if (newChartDetails && newChartDetails.chart_unit) {
          this.soilType = newChartDetails.chart_unit;
        }
      },
      deep: true,
      immediate: true
    },
    // Watch for changes to chart legend prop
    chartLegend: {
      handler(newChartLegend) {
        if (newChartLegend) {
          if (Array.isArray(newChartLegend)) {
            this.chartKeys = newChartLegend;
          } else if (typeof newChartLegend === 'object') {
            this.chartKeys = Object.entries(newChartLegend);
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  beforeUnmount() {
    // Clean up resize listener
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    
    // Clean up resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    
    // Clean up watcher
    // Cancel watcher when component is destroyed
    if (this.watcherUnsubscribe) {
      this.watcherUnsubscribe();
    }
    
    // Clear soil analysis context when leaving the page
    if (this.$root.soilAnalysisContext) {
      this.$root.soilAnalysisContext = null;
    }
  },
  methods: {
    async loadChart() {
      const response = await axios.get(
        route("app.soil-analysis.chart", {
          soil_type: this.selectedSoilType,
          farm: this.farm,
          show_by: this.result_by,
          task: this.task,
        })
      );
      
      this.statistics = response.data.statistics;
      this.$root.sampleZones = response.data.mapData;
      this.chartKeys = response.data.chartLegend;
      
      // Add slight delay to reduce jarring visual effect when changing soil properties
      this.$nextTick(() => {
        setTimeout(() => {
          this.chartKey++; // Force re-render after data update
        }, 50); // Small delay to let data settle
      });
      
      // Update soil analysis context for prop system 
      if (this.task && this.farm && this.soilType) {
        // Preserve the existing context if it exists to avoid overriding user's current selection
        const existingContext = this.$root.soilAnalysisContext;
        this.$root.soilAnalysisContext = {
          task: this.task,
          farm: this.farm,
          soilTypes: this.soilType,
          selectedSoilType: existingContext?.selectedSoilType || this.selectedSoilType
        };
      }
      
    },
  },
};
</script>
